import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail,
  CheckCircle2,
  Eye,
  MousePointerClick,
  XCircle,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
} from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getMailjetStats, getAdminMe } from '@/lib/api';

const statusStyle = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'sent' || s === 'opened' || s === 'clicked')
    return 'bg-green-100 text-green-800 border-green-200';
  if (s === 'queued' || s === 'deferred')
    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  if (s === 'softbounced')
    return 'bg-orange-100 text-orange-800 border-orange-200';
  if (s === 'blocked' || s === 'hardbounced' || s === 'spam')
    return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-gray-100 text-gray-800 border-gray-200';
};

const MetricCard = ({ icon: Icon, label, value, color, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{label}</p>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color.bg}`}>
        <Icon className={`w-5 h-5 ${color.text}`} />
      </div>
    </div>
  </motion.div>
);

const AdminDeliverability = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setRefreshing(true);
      setError(null);
      await getAdminMe();
      const res = await getMailjetStats();
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin');
        return;
      }
      setError(err.response?.data?.detail || err.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <AdminLayout title="Délivrabilité e-mails">
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-terracotta border-t-transparent" />
        </div>
      </AdminLayout>
    );
  }

  const stats = data?.stats || {};
  const sender = data?.sender || {};
  const dns = data?.dns;
  const recent = data?.recent_messages || [];

  const total = stats.total || 1;
  const deliveryRate = stats.total > 0
    ? Math.round(((stats.sent || 0) / total) * 100)
    : 0;
  const issueCount = (stats.blocked || 0) + (stats.soft_bounced || 0) + (stats.hard_bounced || 0);

  return (
    <AdminLayout title="Délivrabilité e-mails">
      <div className="space-y-6" data-testid="admin-deliverability">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm text-gray-600">
              Statistiques en direct depuis l'API Mailjet. Actualisation manuelle.
            </p>
          </div>
          <Button
            onClick={load}
            disabled={refreshing}
            variant="outline"
            data-testid="refresh-mailjet-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Rafraîchir
          </Button>
        </div>

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-900">Erreur API Mailjet</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Configuration expéditeur + DNS */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Mail className="w-4 h-4 text-terracotta" />
                Configuration expéditeur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">From</span>
                <span className="font-mono font-medium text-gray-900">
                  {sender.sender_email}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Name</span>
                <span className="text-gray-900">{sender.sender_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Reply-To</span>
                <span className="font-mono text-gray-900">{sender.reply_to}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">CC auto</span>
                <span className="font-mono text-gray-900">{sender.cc_email}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                {dns?.spf_status === 'OK' && dns?.dkim_status === 'OK' ? (
                  <ShieldCheck className="w-4 h-4 text-green-600" />
                ) : (
                  <ShieldAlert className="w-4 h-4 text-orange-600" />
                )}
                Authentification du domaine
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {dns ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Domaine</span>
                    <span className="font-mono text-gray-900">{dns.domain}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">SPF</span>
                    <Badge className={dns.spf_status === 'OK' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {dns.spf_status}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">DKIM</span>
                    <Badge className={dns.dkim_status === 'OK' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {dns.dkim_status}
                    </Badge>
                  </div>
                  {dns.spf_status === 'OK' && dns.dkim_status === 'OK' && (
                    <p className="text-xs text-green-700 bg-green-50 p-2 rounded mt-2">
                      Votre domaine est authentifié. Les e-mails sont correctement signés et devraient être acceptés par Gmail, Yahoo, Outlook, etc.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  Aucune info DNS disponible pour {sender.sender_email?.split('@')[1]}.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Stats globales */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">
            Statistiques globales (cumul du compte)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              icon={CheckCircle2}
              label="Envoyés acceptés"
              value={stats.sent || 0}
              color={{ bg: 'bg-green-100', text: 'text-green-600' }}
              subtitle={`${deliveryRate}% de délivrabilité`}
            />
            <MetricCard
              icon={Eye}
              label="Ouverts"
              value={stats.opened || 0}
              color={{ bg: 'bg-blue-100', text: 'text-blue-600' }}
              subtitle={stats.sent > 0 ? `${Math.round((stats.opened / stats.sent) * 100)}% taux d'ouverture` : ''}
            />
            <MetricCard
              icon={MousePointerClick}
              label="Clics"
              value={stats.clicked || 0}
              color={{ bg: 'bg-purple-100', text: 'text-purple-600' }}
              subtitle={stats.opened > 0 ? `${Math.round((stats.clicked / stats.opened) * 100)}% des ouvertures` : ''}
            />
            <MetricCard
              icon={AlertTriangle}
              label="Problèmes"
              value={issueCount}
              color={{ bg: 'bg-orange-100', text: 'text-orange-600' }}
              subtitle={`Blocked: ${stats.blocked || 0} · Soft: ${stats.soft_bounced || 0} · Hard: ${stats.hard_bounced || 0}`}
            />
          </div>
        </div>

        {/* Détail erreurs */}
        {(stats.blocked > 0 || stats.soft_bounced > 0 || stats.spam > 0) && (
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-orange-900">
                <AlertTriangle className="w-4 h-4" />
                Points d'attention
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-orange-900 space-y-1">
              {stats.blocked > 0 && (
                <p>• <strong>{stats.blocked}</strong> e-mail{stats.blocked > 1 ? 's' : ''} bloqué{stats.blocked > 1 ? 's' : ''} (adresse invalide ou domaine destinataire refuse).</p>
              )}
              {stats.soft_bounced > 0 && (
                <p>• <strong>{stats.soft_bounced}</strong> softbounce{stats.soft_bounced > 1 ? 's' : ''} (refus temporaire : boîte pleine, serveur lent…).</p>
              )}
              {stats.spam > 0 && (
                <p>• <strong>{stats.spam}</strong> plainte{stats.spam > 1 ? 's' : ''} spam — à surveiller.</p>
              )}
              <p className="text-xs text-orange-800 mt-2 pt-2 border-t border-orange-200">
                Astuce : supprimez les contacts bloqués dans Mailjet pour améliorer votre réputation d'envoi.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Derniers messages */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Derniers envois ({recent.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {recent.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun envoi récent.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="mailjet-recent-table">
                  <thead>
                    <tr className="text-left text-xs uppercase text-gray-500 border-b">
                      <th className="py-2 pr-4">Destinataire</th>
                      <th className="py-2 pr-4">Statut</th>
                      <th className="py-2 pr-4">Spam</th>
                      <th className="py-2 pr-4">Reçu le</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((m) => (
                      <tr key={m.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-2 pr-4 font-mono text-xs">{m.email || '—'}</td>
                        <td className="py-2 pr-4">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${statusStyle(m.status)}`}>
                            {m.status}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-xs text-gray-600">
                          {m.spam_score > 0 ? (
                            <span className="text-orange-600 font-medium">{m.spam_score}</span>
                          ) : (
                            <XCircle className="w-3 h-3 text-gray-300 inline" />
                          )}
                        </td>
                        <td className="py-2 pr-4 text-xs text-gray-500">
                          {m.arrived_at ? new Date(m.arrived_at).toLocaleString('fr-FR') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDeliverability;
